import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useViewMode } from '../../context/ViewModeContext';
import { getPetComponent } from '../pets';
import { getCosmeticComponent } from '../cosmetics';
import { loadModelManifest, modelSnapshot, petModelUrls, cosmeticModelUrls } from './models3d';
import { RELEASE_PETS } from '../release/releaseCatalog';
import { resolvePetId, PETS } from '../../data/pets';

/**
 * The animators' turntable GIFs for the Heart Tree update pets. In 3D these are shown
 * rather than the model drawn here: the site's viewer draws a model without
 * its rest pose, which some of these rigs need to hold together (Star
 * Sprite and Duck Parade came out in pieces), and the animation is the
 * better picture anyway.
 */
const ANIMATED_3D = Object.fromEntries(
  RELEASE_PETS.filter((p) => p.gif).flatMap((p) => [[p.id, p.gif], [petModelUrls(p.id).id, p.gif]]),
);

const BedrockModelPreview3D = lazy(() => import('../release/BedrockModelPreview3D'));

/**
 * A 3D model as a still (for grids) or live and turning (for a page's main
 * picture), falling back to the 2D art while it loads or if it cannot.
 */
function Model({ urls, size, live, fallback, onMissing }) {
  const [snapshot, setSnapshot] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (live) return undefined;
    let cancelled = false;
    setSnapshot(null);
    setFailed(false);
    modelSnapshot(urls.geoUrl, urls.textureUrl, Math.min(512, Math.max(128, Math.round(size * 2)))).then((url) => {
      if (cancelled) return;
      if (url) setSnapshot(url);
      else { setFailed(true); onMissing?.(); }
    });
    return () => { cancelled = true; };
  }, [urls.geoUrl, urls.textureUrl, size, live, onMissing]);

  if (failed) return fallback;
  if (live) {
    return (
      <Suspense fallback={fallback}>
        <BedrockModelPreview3D geoUrl={urls.geoUrl} textureUrl={urls.textureUrl} width={size} height={size}
                               onError={() => { setFailed(true); onMissing?.(); }} />
      </Suspense>
    );
  }
  if (!snapshot) return <span style={{ opacity: 0.35, display: 'block' }}>{fallback}</span>;
  return <img src={snapshot} width={size} height={size} alt="" draggable={false} style={{ display: 'block', objectFit: 'contain' }} />;
}

/** The sets of pets and cosmetics with models, or null until loaded. */
export function useModels() {
  const [models, setModels] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadModelManifest().then((m) => { if (!cancelled) setModels(m); });
    return () => { cancelled = true; };
  }, []);
  return models;
}

/**
 * In 3D, only what has a model: a list of pets filtered to those that can be
 * shown in 3D, and the same for cosmetics. In 2D, or until the lists load,
 * everything.
 */
export function useIn3dOnly() {
  const { mode } = useViewMode();
  const models = useModels();
  const on = mode === '3d' && models !== null;
  return {
    on,
    pet: (id) => !on || models.pets.has(petModelUrls(id).id),
    cosmetic: (id) => !on || models.cosmetics.has(cosmeticModelUrls(id).id),
  };
}

/**
 * The pets a surface shows: its own choice in 2D, and in 3D the same number
 * of pets that have models, so 3D never shows a flat picture in a line of
 * models. Its own picks that have models come first.
 */
export function usePetSelection(preferred, count = preferred.length) {
  const in3d = useIn3dOnly();
  if (!in3d.on) return preferred.slice(0, count);
  const withModels = preferred.filter(in3d.pet);
  const more = PETS.map((p) => p.id).filter((id) => in3d.pet(id) && !withModels.includes(id));
  return [...withModels, ...more].slice(0, count);
}

/** Whether a pet has a 3D model on the site: null until known. */
export function usePetHas3d(petId) {
  const [has3d, setHas3d] = useState(null);
  const { id } = petModelUrls(petId);
  useEffect(() => {
    let cancelled = false;
    loadModelManifest().then((m) => { if (!cancelled) setHas3d(m.pets.has(id)); });
    return () => { cancelled = true; };
  }, [id]);
  return has3d;
}

/** A pet in whichever of 2D or 3D the visitor chose. */
export function PetVisual({ petId, size = 96, mood, live = false, wearsCosmetics, color, showMissing = false, forceMode, clip, clipKey }) {
  // A surface that only makes sense one way (a chat avatar, the dress-up
  // canvas) passes forceMode; everything else follows the switch.
  const { mode: chosen } = useViewMode();
  const mode = forceMode || chosen;
  const has3d = usePetHas3d(petId);
  const urls = petModelUrls(petId);

  const Art = getPetComponent(petId);
  const art = Art ? <Art size={size} mood={mood} wearsCosmetics={wearsCosmetics} color={color} clip={clip} clipKey={clipKey} /> : null;
  if (mode !== '3d') return art;
  const animated = ANIMATED_3D[petId] || ANIMATED_3D[resolvePetId(petId)];
  if (animated) {
    return (
      <img src={animated} width={size} height={size} alt="" draggable={false} loading="lazy"
           style={{ display: 'block', objectFit: 'contain', imageRendering: 'pixelated' }} />
    );
  }
  if (has3d === null) return art;
  if (!has3d) {
    return showMissing ? (
      <span style={{ position: 'relative', display: 'inline-block' }}>
        {art}
        <span className="view-missing-note">No 3D model yet</span>
      </span>
    ) : art;
  }
  return <Model urls={urls} size={size} live={live} fallback={art} />;
}

/** A cosmetic in whichever of 2D or 3D the visitor chose. */
export function CosmeticVisual({ cosmeticId, size = 72, live = false, fallback, forceMode }) {
  const { mode: chosen } = useViewMode();
  const mode = forceMode || chosen;
  const Art = getCosmeticComponent(cosmeticId);
  const art = Art ? <Art size={size} /> : (fallback || null);
  if (mode !== '3d') return art;
  return <Model urls={cosmeticModelUrls(cosmeticId)} size={size} live={live} fallback={art} />;
}
